import React, { useState, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import SelectableEntityCardSwitch from 'components/IAP/cards/SelectableEntityCardSwitch';
import { difference } from 'lodash';

interface Resource {
  id: string;
  name?: string;
  current_status?: string;
  resource_type?: string;
  description?: string;
  quantity?: number;
  // Add other known properties as needed
}

interface ResourceAssignmentFieldProps {
  memberResources: Resource[];
  availableResources: Resource[];
  hasCheckedInResources: boolean;
}

const ResourceAssignmentField: React.FC<ResourceAssignmentFieldProps> = ({
  memberResources,
  availableResources,
  hasCheckedInResources,
}) => {
  const { control } = useFormContext<{ resources: string[] }>();
  const { field } = useController({
    name: 'resources',
    control,
    defaultValue: memberResources.map((r) => r.id),
  });
  const { onChange, value } = field;

  const [textFilter, setTextFilter] = useState('');

  // Filter out checked_out and in_process resources unless already selected
  const filteredResources = useMemo(() => {
    const currentResourceIds = memberResources.map((r) => r.id);
    return availableResources.filter((resource) => {
      // Always show resources that are already selected
      if (currentResourceIds.includes(resource.id)) return true;

      // Filter out checked_out and in_process resources
      if (
        resource.current_status === 'checked_out' ||
        resource.current_status === 'in_process'
      )
        return false;

      return true;
    });
  }, [availableResources, memberResources]);

  // Get selected and unselected resources
  const selectedResources = value
    .map((id: string) => {
      const found = filteredResources.find((r) => r.id === id);
      return found;
    })
    .filter((x): x is Resource => !!x);

  const unselectedResources = difference(
    filteredResources,
    selectedResources
  ).filter((resource) => {
    return (
      resource.name?.toLowerCase().includes(textFilter.toLowerCase()) ?? true
    );
  });

  const toggleRelationship = (resource: Resource) => {
    if (isDisabled) {
      // Don't allow changes when resources are checked in
      return;
    }
    const resourceId = resource.id;
    if (value.includes(resourceId)) {
      onChange(value.filter((id: string) => id !== resourceId));
    } else {
      onChange([...value, resourceId]);
    }
  };

  // Note: This component reimplements RelationshipPickerField's functionality
  // to show resources immediately without requiring a double-click.
  // The hasCheckedInResources prop controls whether the field should be disabled
  // to prevent modifications when resources are checked in.
  const isDisabled = hasCheckedInResources;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="txt--semibold pt-4">
          Assigned Resources: {value.length ?? 0}
        </div>
        <div className="d-flex align-items-center">
          <StylishNewSearchBar
            value={textFilter}
            onChangeSearchTerm={isDisabled ? () => {} : setTextFilter}
            icon={'filter_alt'}
            placeholder={isDisabled ? 'Filtering disabled' : 'Filter...'}
            className={'pb-0'}
          />
        </div>
      </div>
      {isDisabled && (
        <div className="alert alert-info mb-2">
          Resources cannot be modified while they are checked in. Please check
          out resources through the 221 checkout process.
        </div>
      )}
      <div className="d-flex flex-column gap-2">
        {selectedResources.map((entity) => (
          <SelectableEntityCardSwitch
            key={entity.id}
            entity={entity}
            value={value}
            toggleRelationship={toggleRelationship}
            cardType="resource-set"
          />
        ))}
      </div>
      {unselectedResources.length > 0 && (
        <div className="d-flex flex-column gap-2 mt-3">
          {unselectedResources.map((entity) => (
            <SelectableEntityCardSwitch
              key={entity.id}
              entity={entity}
              value={value}
              toggleRelationship={toggleRelationship}
              cardType="resource-set"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceAssignmentField;
